import React, { useState } from 'react';
import { LearningSummary } from '../types/analytics.types';
import './ComponentPerformance.css';

interface ComponentPerformanceProps {
  data: LearningSummary;
  loading?: boolean;
}

export const ComponentPerformance: React.FC<ComponentPerformanceProps> = ({ data, loading }) => {
  const [sortBy, setSortBy] = useState<'usage' | 'name'>('usage');

  if (loading) {
    return (
      <div className="component-performance">
        <h4>Component Usage</h4>
        <div className="loading-skeleton" style={{ height: '200px' }}></div>
      </div>
    );
  }

  if (!data || !data.top_components || data.top_components.length === 0) {
    return (
      <div className="component-performance">
        <h4>Component Usage</h4>
        <p className="no-data">No component data available</p>
      </div>
    );
  }

  const sortedComponents = [...data.top_components].sort((a, b) => {
    if (sortBy === 'usage') {
      return b.usage_count - a.usage_count;
    }
    return a.component.localeCompare(b.component);
  });

  const totalUsage = data.top_components.reduce((sum, c) => sum + c.usage_count, 0);

  // Component emoji mapping
  const componentEmojis: { [key: string]: string } = {
    'Flashcards': '🃏',
    'Matching': '🔗',
    'Memory': '🧠',
    'Fill in the blank': '📝',
    'Multiple Choice': '✅',
    'Pronunciation': '🗣️',
    'Passage': '📖',
    'Conversation': '💬',
    'Item Guess': '🎯',
    'Item Describe': '🖼️',
    'SpeedGame': '⚡',
    'Sentence Translation': '🔄'
  };

  return (
    <div className="component-performance">
      <div className="performance-header">
        <h4>Component Usage & Performance</h4>
        <div className="sort-selector">
          <button
            className={sortBy === 'usage' ? 'active' : ''}
            onClick={() => setSortBy('usage')}
          >
            By Usage
          </button>
          <button
            className={sortBy === 'name' ? 'active' : ''}
            onClick={() => setSortBy('name')}
          >
            By Name
          </button>
        </div>
      </div>

      <div className="components-grid">
        {sortedComponents.map((component) => {
          const percentage = ((component.usage_count / totalUsage) * 100).toFixed(1);
          const emoji = componentEmojis[component.component] || '📊';

          return (
            <div key={component.component} className="component-card">
              <div className="component-header">
                <span className="component-emoji">{emoji}</span>
                <span className="component-name">{component.component}</span>
              </div>
              <div className="component-stats">
                <div className="stat">
                  <span className="stat-value">{component.usage_count.toLocaleString()}</span>
                  <span className="stat-label">uses</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{percentage}%</span>
                  <span className="stat-label">of total</span>
                </div>
              </div>
              <div className="usage-bar">
                <div
                  className="usage-fill"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="performance-summary">
        <div className="summary-stat">
          <span className="summary-label">Total Components</span>
          <span className="summary-value">{data.top_components.length}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Total Usage</span>
          <span className="summary-value">{totalUsage.toLocaleString()}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Avg per Component</span>
          <span className="summary-value">
            {Math.round(totalUsage / data.top_components.length).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ComponentPerformance;
